import './Footer.scss';

import cn from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';

import Button from '@/common/components/atoms/Button/Button';
import ExternalLink from '@/common/components/atoms/ExternalLink/ExternalLink';
import Picture from '@/common/components/atoms/Picture/Picture';
import NavLinkExtended from '@/common/components/blocks/NavLinkExtended/NavLinkExtended';
import Accordion from '@/common/components/molecules/Accordion/Accordion';
import {
  COMPANIES_LIST,
  LINK_AUBADE,
  LINK_TARIFEO,
  ROUTE_ACCOUNT_DOCUMENTATION,
  ROUTE_CONTACT,
  ROUTE_ECATALOGS,
  ROUTE_LEGALS,
  ROUTE_NODES_CLEARANCE,
  ROUTE_NODES_DISCOUNT,
  ROUTE_ORDERS,
  ROUTE_PARTNERS,
  ROUTE_QUOTES,
} from '@/common/constants';
import { useAuth } from '@/common/contexts/AuthContext';
import { useConfig } from '@/common/contexts/ConfigContext';
import { useModalContext } from '@/common/contexts/ModalContext';
import formatSrcSet from '@/common/helpers/formatSrcSet';
import getImageBySize from '@/common/helpers/getImageBySize';
import usePromotionBlock from '@/common/hooks/promotion-block/usePromotionBlock';
import { ReactComponent as LogoEA } from '@/ebat/assets/logos/espace-aubade.svg';
import { ReactComponent as LogoLeMaterielElectrique } from '@/ebat/assets/logos/le-materiel-electrique.svg';
import { ReactComponent as LogoTarifeo } from '@/ebat/assets/logos/tarifeo.svg';
import { FOOTER_PROMOTION_BLOCK_FORMATS } from '@/ebat/constants';

const Footer = () => {
  const { t } = useTranslation();
  const { isChrono } = useConfig();
  const { showClearance, showDoc, showECatalog, showPromo, user } = useAuth();
  const { setCookieModalIsOpen } = useModalContext();

  const [promotion, setPromotion] = useState(null);
  const [hasCompanyLogo, setHasCompanyLogo] = useState(true);

  const { data: promotionBlock, isSuccess } = usePromotionBlock({
    type: 'insert',
    position: 'footer',
    limit: 10,
  });

  /**
   * Generate logo src with company code
   * @type {string}
   */
  const getCompanyLogo = useMemo(
    () =>
      user?.company_code
        ? `${process.env.REACT_APP_MEDIA_BASE_URL}/ebat/company-logo/logo_${user.company_code}.svg`
        : null,
    [user],
  );

  /**
   * Get company website with company code
   * @type {string}
   */
  const getCompanyWebsite = useMemo(
    () =>
      user?.company_code ? COMPANIES_LIST[user.company_code]?.website : null,
    [user],
  );

  useEffect(() => {
    if (isSuccess && promotionBlock.data && promotionBlock.data.length > 0) {
      const randomItem = Math.floor(Math.random() * promotionBlock.data.length);

      setPromotion(promotionBlock.data[randomItem]);
    }
  }, [isSuccess, promotionBlock]);

  const ProductsList = () => (
    <ul className="list__items" data-qa-id="footer-products">
      {showPromo && (
        <li className="list__item" data-qa-id="footer-products-item">
          <NavLink
            className="list__link"
            to={ROUTE_NODES_DISCOUNT}
            data-qa-id="discount"
          >
            {t('ebat:entities:layout.footer.products.promotions')}
          </NavLink>
        </li>
      )}
      {showClearance && (
        <li className="list__item" data-qa-id="footer-products-item">
          <NavLink
            className="list__link"
            to={ROUTE_NODES_CLEARANCE}
            data-qa-id="clearance"
          >
            {t('ebat:entities:layout.footer.products.clearance')}
          </NavLink>
        </li>
      )}
      <li className="list__item" data-qa-id="footer-products-item">
        <NavLink
          className="list__link"
          to={ROUTE_PARTNERS}
          data-qa-id="partners"
        >
          {t('ebat:entities:layout.footer.products.partners')}
        </NavLink>
      </li>
    </ul>
  );

  const OrdersList = () => (
    <ul className="list__items" data-qa-id="footer-orders">
      <li
        className={cn('list__item', {
          'list__item--disabled': !user?.functionality_accesses?.orders,
        })}
        data-qa-id="footer-orders-item"
      >
        <NavLinkExtended
          className="list__link"
          disabled={!user?.functionality_accesses?.orders}
          to={ROUTE_ORDERS}
          context="orders"
        >
          {t('ebat:entities:layout.footer.orders.track_your_orders')}
        </NavLinkExtended>
      </li>
      {!isChrono && (
        <li
          className={cn('list__item', {
            'list__item--disabled': !user?.functionality_accesses?.quotes,
          })}
          data-qa-id="footer-orders-item"
        >
          <NavLinkExtended
            className="list__link"
            disabled={!user?.functionality_accesses?.quotes}
            to={ROUTE_QUOTES}
            context="quotes"
          >
            {t('ebat:entities:layout.footer.orders.track_your_quotes')}
          </NavLinkExtended>
        </li>
      )}
    </ul>
  );

  const ServicesList = () => (
    <>
      <ul className="list__items" data-qa-id="footer-services">
        {showECatalog && (
          <li className="list__item" data-qa-id="footer-services-item">
            <NavLink
              className="list__link"
              to={ROUTE_ECATALOGS}
              data-qa-id="ecatalogs"
            >
              {t('ebat:entities:layout.footer.services.ecatalogs')}
            </NavLink>
          </li>
        )}
        {showDoc && (
          <li className="list__item" data-qa-id="footer-services-item">
            <NavLink
              className="list__link"
              to={ROUTE_ACCOUNT_DOCUMENTATION}
              data-qa-id="documentation"
            >
              {t('ebat:entities:layout.footer.services.documentation')}
            </NavLink>
          </li>
        )}
        <li className="list__item" data-qa-id="footer-services-item">
          <NavLink
            className="list__link"
            to={ROUTE_CONTACT}
            data-qa-id="contact"
          >
            {t('ebat:entities:layout.footer.services.contact')}
          </NavLink>
        </li>
      </ul>
      <ul className="list list--logos" data-qa-id="footer-logos">
        {/*TODO : to be deleted if no longer needed*/}
        {/*<li className="list__item">*/}
        {/*  <ExternalLink className="list__link" href="https://www.editherm.com">*/}
        {/*    <LogoEditherm className="list__logo" />*/}
        {/*  </ExternalLink>*/}
        {/*</li>*/}
        <li className="list__item" data-qa-id="footer-logos-item">
          <ExternalLink
            className="list__link"
            href={LINK_TARIFEO}
            data-qa-id="tarifeo"
          >
            <LogoTarifeo className="list__logo" />
          </ExternalLink>
        </li>
      </ul>
    </>
  );

  return (
    <footer className="footer" data-qa-id="footer">
      <div className="footer__top" data-qa-id="footer-top">
        <div className="wrapper">
          <div className="footer__products" data-qa-id="footer-products">
            <div className="list list--with-title">
              <Accordion
                accordionLabel={t('ebat:entities:layout.footer.products.title')}
                isOpen={false}
              >
                <ProductsList />
              </Accordion>
            </div>
          </div>
          <div className="footer__orders" data-qa-id="footer-orders">
            <div className="list list--with-title">
              <Accordion
                accordionLabel={t('ebat:entities:layout.footer.orders.title')}
                isOpen={false}
              >
                <OrdersList />
              </Accordion>
            </div>
          </div>
          <div className="footer__services" data-qa-id="footer-services">
            <div className="list list--with-title">
              <Accordion
                accordionLabel={t('ebat:entities:layout.footer.services.title')}
                isOpen={false}
              >
                <ServicesList />
              </Accordion>
            </div>
          </div>
          <div className="footer__image" data-qa-id="footer-image">
            {promotion && (
              <ExternalLink
                key={`promotion_${promotion.id}`}
                href={promotion.link}
              >
                {promotion?.images?.length > 0 && (
                  <Picture
                    alt={promotion.images[0].alt}
                    className="img-footer"
                    src={getImageBySize(
                      promotion.images[0].url,
                      FOOTER_PROMOTION_BLOCK_FORMATS[
                        promotion.images[0].description
                      ],
                    )}
                    srcSet={formatSrcSet(
                      promotion.images,
                      FOOTER_PROMOTION_BLOCK_FORMATS,
                    )}
                  />
                )}
              </ExternalLink>
            )}
          </div>
        </div>
      </div>
      <div className="footer__bottom" data-qa-id="footer-bottom">
        <div className="wrapper">
          <div className="footer__legal" data-qa-id="footer-legal">
            <NavLink className="link" to={ROUTE_LEGALS}>
              {t('ebat:entities:layout.footer.cgv')}
            </NavLink>
            <Button className="link" onClick={() => setCookieModalIsOpen(true)}>
              {t('ebat:entities:layout.footer.cookies')}
            </Button>
          </div>
          <div className="footer__logos" data-qa-id="footer-logos">
            <ul className="list list--logos" data-qa-id="footer-logos">
              {hasCompanyLogo && getCompanyLogo && (
                <li
                  className="list__item"
                  data-qa-value="company"
                  data-qa-id="footer-logos-item"
                >
                  <ExternalLink className="list__link" href={getCompanyWebsite}>
                    <Picture
                      alt={user?.company}
                      className="list__logo"
                      onError={() => setHasCompanyLogo(false)}
                      src={getCompanyLogo}
                    />
                  </ExternalLink>
                </li>
              )}
              <li
                className="list__item"
                data-qa-value="ME"
                data-qa-id="footer-logos-item"
              >
                {/*TODO: to be deleted if not used*/}
                {/*<ExternalLink className="list__link" href={LINK_MATERIAUX}>*/}
                <LogoLeMaterielElectrique className="list__logo2" />
                {/*</ExternalLink>*/}
              </li>
              <li
                className="list__item"
                data-qa-value="EA"
                data-qa-id="footer-logos-item"
              >
                <ExternalLink className="list__link" href={LINK_AUBADE}>
                  <LogoEA className="list__logo2" />
                </ExternalLink>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
